import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Plus, Trash2, Eye, RefreshCw, FileText, Send, Archive, CheckCircle2, History, FileOutput,
} from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from './ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from './ui/select';
import { docTemplatesAPI, employeeAPI } from '../services/api';
import { confirmToast } from '../lib/confirmToast';

const CATEGORIES = [
  'offer_letter', 'appointment_letter', 'contract', 'policy', 'certificate',
  'relieving_letter', 'experience_letter', 'warning_letter', 'general',
];
const titleize = (s) => String(s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const statusVariant = (s) => ({ draft: 'secondary', active: 'default', archived: 'outline' }[s] || 'secondary');

export default function DocumentTemplates() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null); // template being edited (null = new)
  const [form, setForm] = useState({ name: '', category: 'general', description: '', body: '', change_note: '' });

  const [renderOpen, setRenderOpen] = useState(false);
  const [renderMode, setRenderMode] = useState('preview'); // preview | generate
  const [renderTemplate, setRenderTemplate] = useState(null);
  const [renderEmployee, setRenderEmployee] = useState('');
  const [renderResult, setRenderResult] = useState(null);
  const [employees, setEmployees] = useState([]);

  const [generatedOpen, setGeneratedOpen] = useState(false);
  const [generated, setGenerated] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (categoryFilter) params.category = categoryFilter;
      if (statusFilter) params.status = statusFilter;
      const { data } = await docTemplatesAPI.list(params);
      setRows(data.data || []);
    } catch (e) { toast.error('Failed to load templates'); }
    finally { setLoading(false); }
  }, [categoryFilter, statusFilter]);

  const loadSummary = useCallback(async () => {
    try { const { data } = await docTemplatesAPI.summary(); setSummary(data); } catch (e) { /* */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  const ensureEmployees = async () => {
    if (employees.length === 0) {
      try {
        const { data } = await employeeAPI.list();
        setEmployees((Array.isArray(data) ? data : data.data || []).filter((e) => e.is_active !== false));
      } catch (e) { /* */ }
    }
  };

  const openNew = () => { setEditing(null); setForm({ name: '', category: 'general', description: '', body: '', change_note: '' }); setEditorOpen(true); };
  const openEdit = async (id) => {
    try {
      const { data } = await docTemplatesAPI.get(id);
      setEditing(data);
      setForm({ name: data.name, category: data.category, description: data.description || '', body: data.body || '', change_note: '' });
      setEditorOpen(true);
    } catch (e) { toast.error('Failed to load template'); }
  };

  const discoveredFields = useMemo(() => {
    const set = new Set();
    (form.body.match(/\{\{\s*([a-zA-Z0-9_.]+)\s*\}\}/g) || []).forEach((m) => set.add(m.replace(/[{}\s]/g, '')));
    return [...set];
  }, [form.body]);

  const saveTemplate = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    try {
      if (editing) {
        await docTemplatesAPI.update(editing.id, {
          name: form.name, category: form.category, description: form.description,
          body: form.body, change_note: form.change_note || undefined,
        });
        toast.success('Template updated');
      } else {
        await docTemplatesAPI.create({ name: form.name, category: form.category, description: form.description, body: form.body });
        toast.success('Template created');
      }
      setEditorOpen(false); load(); loadSummary();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };

  const doPublish = (row) => confirmToast({
    message: `Publish "${row.name}"?`, description: 'Makes it available for document generation.',
    confirmLabel: 'Publish',
    onConfirm: async () => {
      try { await docTemplatesAPI.publish(row.id); toast.success('Published'); load(); loadSummary(); }
      catch (e) { toast.error(e?.response?.data?.detail || 'Publish failed'); }
    },
  });
  const doArchive = (row) => confirmToast({
    message: `Archive "${row.name}"?`, confirmLabel: 'Archive', destructive: true,
    onConfirm: async () => {
      try { await docTemplatesAPI.archive(row.id); toast.success('Archived'); load(); loadSummary(); }
      catch (e) { toast.error('Archive failed'); }
    },
  });
  const doDelete = (row) => confirmToast({
    message: `Delete "${row.name}"?`, description: 'This cannot be undone.', confirmLabel: 'Delete', destructive: true,
    onConfirm: async () => {
      try { await docTemplatesAPI.remove(row.id); toast.success('Deleted'); load(); loadSummary(); }
      catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
    },
  });

  const openRender = async (row, mode) => {
    setRenderTemplate(row); setRenderMode(mode); setRenderEmployee(''); setRenderResult(null); setRenderOpen(true);
    await ensureEmployees();
  };
  const runRender = async () => {
    try {
      const payload = { employee_id: renderEmployee || null, context: {} };
      if (renderMode === 'preview') {
        const { data } = await docTemplatesAPI.preview(renderTemplate.id, payload);
        setRenderResult(data);
      } else {
        const { data } = await docTemplatesAPI.generate(renderTemplate.id, payload);
        setRenderResult({ content: data.content, unresolved_fields: data.unresolved_fields });
        toast.success('Document generated');
        loadSummary();
      }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Render failed'); }
  };

  const openGenerated = async () => {
    setGeneratedOpen(true);
    try { const { data } = await docTemplatesAPI.listGenerated(); setGenerated(data.data || []); }
    catch (e) { toast.error('Failed to load generated documents'); }
  };

  const statCards = useMemo(() => ([
    { label: 'Templates', value: summary?.total_templates ?? '—' },
    { label: 'Active', value: summary?.active ?? '—' },
    { label: 'Generated Docs', value: summary?.generated_documents ?? '—' },
  ]), [summary]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2"><FileText className="h-5 w-5" /> Document Templates</h2>
          <p className="text-sm text-muted-foreground">Reusable templates with merge fields, versioning and generate-from-template.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={openGenerated}><FileOutput className="h-4 w-4 mr-1" /> Generated</Button>
          <Button onClick={openNew}><Plus className="h-4 w-4 mr-1" /> New Template</Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {statCards.map((s) => (
          <Card key={s.label}><CardContent className="p-4">
            <p className="text-xs text-muted-foreground">{s.label}</p>
            <p className="text-2xl font-semibold">{s.value}</p>
          </CardContent></Card>
        ))}
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <div className="flex flex-col gap-1">
          <Label className="text-xs">Category</Label>
          <Select value={categoryFilter || '__all__'} onValueChange={(v) => setCategoryFilter(v === '__all__' ? '' : v)}>
            <SelectTrigger className="h-9 w-44"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All Categories</SelectItem>
              {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{titleize(c)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1">
          <Label className="text-xs">Status</Label>
          <Select value={statusFilter || '__all__'} onValueChange={(v) => setStatusFilter(v === '__all__' ? '' : v)}>
            <SelectTrigger className="h-9 w-40"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All Statuses</SelectItem>
              {['draft', 'active', 'archived'].map((s) => <SelectItem key={s} value={s}>{titleize(s)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" size="sm" className="h-9" onClick={() => { load(); loadSummary(); }}>
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      <div className="border rounded-lg overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Version</TableHead>
              <TableHead>Merge Fields</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No templates.</TableCell></TableRow>
            ) : rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell className="font-medium">{row.name}</TableCell>
                <TableCell>{titleize(row.category)}</TableCell>
                <TableCell><Badge variant={statusVariant(row.status)}>{titleize(row.status)}</Badge></TableCell>
                <TableCell>v{row.current_version}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{(row.merge_fields || []).length}</TableCell>
                <TableCell className="text-right space-x-1 whitespace-nowrap">
                  <Button size="sm" variant="outline" onClick={() => openRender(row, 'preview')} title="Preview"><Eye className="h-3.5 w-3.5" /></Button>
                  {row.status === 'active' && <Button size="sm" variant="outline" onClick={() => openRender(row, 'generate')} title="Generate"><Send className="h-3.5 w-3.5" /></Button>}
                  <Button size="sm" variant="outline" onClick={() => openEdit(row.id)} title="Edit"><History className="h-3.5 w-3.5" /></Button>
                  {row.status === 'draft' && <Button size="sm" variant="outline" onClick={() => doPublish(row)} title="Publish"><CheckCircle2 className="h-3.5 w-3.5" /></Button>}
                  {row.status === 'active' && <Button size="sm" variant="outline" onClick={() => doArchive(row)} title="Archive"><Archive className="h-3.5 w-3.5" /></Button>}
                  <Button size="sm" variant="outline" onClick={() => doDelete(row)}><Trash2 className="h-3.5 w-3.5" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Editor dialog */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editing ? `Edit — ${editing.name} (v${editing.current_version})` : 'New Template'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Name</Label>
                <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
              </div>
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{titleize(c)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Input value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
            </div>
            <div>
              <Label>Body <span className="text-xs text-muted-foreground">— use {'{{field}}'} for merge fields</span></Label>
              <Textarea className="font-mono text-sm min-h-[180px]" value={form.body}
                onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
                placeholder="Dear {{employee_name}}, ..." />
            </div>
            {discoveredFields.length > 0 && (
              <div className="text-xs">
                <span className="text-muted-foreground">Detected merge fields: </span>
                {discoveredFields.map((f) => <Badge key={f} variant="secondary" className="mr-1">{f}</Badge>)}
              </div>
            )}
            {editing && (
              <div>
                <Label className="text-xs">Change note <span className="text-muted-foreground">(saved if body changes → new version)</span></Label>
                <Input value={form.change_note} onChange={(e) => setForm((f) => ({ ...f, change_note: e.target.value }))} />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditorOpen(false)}>Cancel</Button>
            <Button onClick={saveTemplate}>{editing ? 'Save' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview / Generate dialog */}
      <Dialog open={renderOpen} onOpenChange={setRenderOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{renderMode === 'preview' ? 'Preview' : 'Generate'} — {renderTemplate?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Employee (auto-fills merge fields)</Label>
              <Select value={renderEmployee || '__none__'} onValueChange={(v) => setRenderEmployee(v === '__none__' ? '' : v)}>
                <SelectTrigger><SelectValue placeholder="No employee" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No employee</SelectItem>
                  {employees.map((e) => (
                    <SelectItem key={e.employee_id} value={e.employee_id}>
                      {(e.first_name || '') + ' ' + (e.last_name || '')} ({e.employee_id})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" onClick={runRender}>
              {renderMode === 'preview' ? 'Render Preview' : 'Generate Document'}
            </Button>
            {renderResult && (
              <div className="space-y-2">
                {renderResult.unresolved_fields?.length > 0 && (
                  <p className="text-xs text-amber-600">
                    Unresolved: {renderResult.unresolved_fields.join(', ')}
                  </p>
                )}
                <pre className="border rounded p-3 bg-muted/40 text-sm whitespace-pre-wrap max-h-[320px] overflow-y-auto">{renderResult.content}</pre>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenderOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generated documents dialog */}
      <Dialog open={generatedOpen} onOpenChange={setGeneratedOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Generated Documents</DialogTitle></DialogHeader>
          <div className="max-h-[420px] overflow-y-auto space-y-2">
            {generated.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">No generated documents yet.</p>
            ) : generated.map((g) => (
              <div key={g.id} className="border rounded p-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">{g.title || `Doc #${g.id}`}</span>
                  <span className="text-xs text-muted-foreground">{g.employee_id || '—'} · v{g.template_version} · {String(g.created_at).slice(0, 10)}</span>
                </div>
                <pre className="text-xs whitespace-pre-wrap mt-1 text-muted-foreground max-h-24 overflow-y-auto">{g.content}</pre>
              </div>
            ))}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setGeneratedOpen(false)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
